import type { WorkflowResponse } from '@state/workflow.ts'

const paletteColors = [
  { bg: 'bg-[#8ecae6]', hover: 'hover:bg-[#6db5d8]', tailwindBg: '#8ecae6' },
  { bg: 'bg-[#219ebc]', hover: 'hover:bg-[#1a7d96]', tailwindBg: '#219ebc' },
  { bg: 'bg-[#023047]', hover: 'hover:bg-[#012232]', tailwindBg: '#023047' },
  { bg: 'bg-[#ffb703]', hover: 'hover:bg-[#e6a502]', tailwindBg: '#ffb703' },
  { bg: 'bg-[#fb8500]', hover: 'hover:bg-[#e07600]', tailwindBg: '#fb8500' },
]

export function getWorkflowTypeColor(workflowType: string): { bg: string; hover: string; tailwindBg: string } {
  // Otherwise, assign a color from the palette based on a hash of the workflow type
  const hash = workflowType.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
  const colorIndex = hash % paletteColors.length
  return paletteColors[colorIndex]!
}

export function formatDuration(startDate: string, endDate: string): string {
  const start = new Date(startDate).getTime()
  const end = new Date(endDate).getTime()
  const diffMs = end - start

  const seconds = Math.floor(diffMs / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)

  if (days > 0) {
    return `${days}d ${hours % 24}h ${minutes % 60}m`
  } else if (hours > 0) {
    return `${hours}h ${minutes % 60}m ${seconds % 60}s`
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`
  } else {
    return `${seconds}s`
  }
}

export function formatDurationMs(durationMs: number): string {
  const seconds = Math.floor(durationMs / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)

  if (days > 0) {
    return `${days}d ${hours % 24}h ${minutes % 60}m`
  } else if (hours > 0) {
    return `${hours}h ${minutes % 60}m ${seconds % 60}s`
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`
  } else {
    return `${seconds}s`
  }
}

export function getTimeInterval(durationMs: number): { interval: number; label: string } {
  const durationMinutes = durationMs / (1000 * 60)
  const durationHours = durationMinutes / 60

  if (durationHours >= 24) {
    return { interval: 2 * 60 * 60 * 1000, label: 'h' }
  } else if (durationHours >= 6) {
    return { interval: 60 * 60 * 1000, label: 'h' }
  } else if (durationHours >= 3) {
    return { interval: 15 * 60 * 1000, label: 'min' }
  } else if (durationMinutes >= 30) {
    return { interval: 5 * 60 * 1000, label: 'min' }
  } else if (durationMinutes >= 10) {
    return { interval: 60 * 1000, label: 'min' }
  } else {
    return { interval: 30 * 1000, label: 's' }
  }
}

export function formatTimeMarker(date: Date, label: string): string {
  if (label === 'h') {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  } else if (label === 'min') {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  } else {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
  }
}

export function assignWorkflowRows(workflows: WorkflowResponse[]): WorkflowResponse[][] {
  const sorted = [...workflows].sort((a, b) =>
    new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  )

  const now = Date.now()
  const rows: WorkflowResponse[][] = []
  const rowEndTimes: number[] = []

  sorted.forEach(workflow => {
    const start = new Date(workflow.createdAt).getTime()
    // Use current time for running workflows, otherwise use updatedAt
    const end = workflow.status === 'RUNNING' ? now : new Date(workflow.updatedAt).getTime()

    let assignedRow = -1
    for (let i = 0; i < rowEndTimes.length; i++) {
      if (start >= rowEndTimes[i]!) {
        assignedRow = i
        rowEndTimes[i] = end
        break
      }
    }

    if (assignedRow === -1) {
      assignedRow = rowEndTimes.length
      rowEndTimes.push(end)
      rows.push([])
    }

    rows[assignedRow]!.push(workflow)
  })

  return rows
}